import { useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Placeholder } from "@tiptap/extensions";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { toHtml, absolutize, relativize } from "../lib/richText";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Link2, ImagePlus, Undo2, Redo2, Loader2 } from "lucide-react";

const HTTPS = /^https:\/\/[^\s/]+\.[^\s]+/;

const ToolBtn = ({ testId, label, active, disabled, onClick, children }) => (
  <button type="button" data-testid={testId} aria-label={label} title={label} aria-pressed={!!active} disabled={disabled}
    onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${active ? "bg-[#4E342E] text-white" : "text-[#5D4037] hover:bg-[#EDE7DC]"}`}>{children}</button>
);
const Sep = () => <span className="mx-1 h-5 w-px bg-[#E5DFD3]" aria-hidden="true" />;

function LinkBar({ editor, testId, onClose }) {
  const [url, setUrl] = useState(editor.getAttributes("link").href || "https://");
  const apply = () => {
    const v = url.trim();
    if (!HTTPS.test(v)) { toast.error("Links must start with https://"); return; }
    const chain = editor.chain().focus();
    if (editor.state.selection.empty && !editor.isActive("link")) chain.insertContent({ type: "text", text: v, marks: [{ type: "link", attrs: { href: v } }] }).run();
    else chain.extendMarkRange("link").setLink({ href: v }).run();
    onClose();
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#EFEBE0] bg-[#FFFDF7] px-3 py-2" data-testid={`${testId}-linkbar`}>
      <input data-testid={`${testId}-link-url`} autoFocus value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } if (e.key === "Escape") onClose(); }}
        aria-label="Link address" className="h-8 min-w-[220px] flex-1 rounded-lg border border-[#E5DFD3] px-2.5 text-sm text-[#3E2723] outline-none focus:border-[#7B6157]" />
      <button type="button" data-testid={`${testId}-link-apply`} onClick={apply} className="rounded-full bg-[#4E342E] px-3 py-1 text-xs font-semibold text-white">Apply</button>
      {editor.isActive("link") && <button type="button" data-testid={`${testId}-link-remove`} onClick={() => { editor.chain().focus().extendMarkRange("link").unsetLink().run(); onClose(); }}
        className="rounded-full px-3 py-1 text-xs font-semibold text-[#C62828] hover:bg-[#FFEBEE]">Remove link</button>}
      <button type="button" onClick={onClose} className="rounded-full px-3 py-1 text-xs font-semibold text-[#6D4C41] hover:bg-[#F5F2EB]">Cancel</button>
    </div>
  );
}

/** Formatted handout editor: bold, italic, underline, headings, lists, https links, and uploaded images. */
export function RichTextEditor({ value, onChange, testId, placeholder = "Write the handout here", label = "Handout text" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [linking, setLinking] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, code: false, codeBlock: false, blockquote: false, horizontalRule: false, strike: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: "https", isAllowedUri: (u) => /^https:\/\//.test(u), HTMLAttributes: { rel: "noopener noreferrer nofollow", target: null } } }),
      Image, Placeholder.configure({ placeholder }),
    ],
    content: absolutize(toHtml(value)),
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: ed }) => onChange(relativize(ed.getHTML())),
    editorProps: { attributes: { class: "cocoa-prose min-h-[260px] max-h-[60vh] overflow-y-auto px-4 py-3", "data-testid": testId, "aria-label": label, role: "textbox", "aria-multiline": "true" } },
  });
  if (!editor) return null;
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Images must be under 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/handout-images", fd);
      editor.chain().focus().setImage({ src: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`, alt: file.name.replace(/\.[^.]+$/, "").slice(0, 120) }).run();
      toast.success("Image added");
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };
  const run = (fn) => () => fn(editor.chain().focus()).run();
  const words = editor.getText().trim().split(/\s+/).filter(Boolean).length;
  return (
    <div className="rounded-lg border border-[#E5DFD3] bg-white focus-within:ring-2 focus-within:ring-[#8D6E63]" data-testid={`${testId}-wrap`}>
      <div role="toolbar" aria-label="Formatting" className="flex flex-wrap items-center gap-0.5 rounded-t-lg border-b border-[#EFEBE0] bg-[#FDFCF8] px-2 py-1.5">
        <ToolBtn testId={`${testId}-bold`} label="Bold" active={editor.isActive("bold")} onClick={run((c) => c.toggleBold())}><Bold size={15} /></ToolBtn>
        <ToolBtn testId={`${testId}-italic`} label="Italic" active={editor.isActive("italic")} onClick={run((c) => c.toggleItalic())}><Italic size={15} /></ToolBtn>
        <ToolBtn testId={`${testId}-underline`} label="Underline" active={editor.isActive("underline")} onClick={run((c) => c.toggleUnderline())}><Underline size={15} /></ToolBtn>
        <Sep />
        <ToolBtn testId={`${testId}-h2`} label="Section heading" active={editor.isActive("heading", { level: 2 })} onClick={run((c) => c.toggleHeading({ level: 2 }))}><Heading2 size={16} /></ToolBtn>
        <ToolBtn testId={`${testId}-h3`} label="Small heading" active={editor.isActive("heading", { level: 3 })} onClick={run((c) => c.toggleHeading({ level: 3 }))}><Heading3 size={16} /></ToolBtn>
        <Sep />
        <ToolBtn testId={`${testId}-bullets`} label="Bulleted list" active={editor.isActive("bulletList")} onClick={run((c) => c.toggleBulletList())}><List size={16} /></ToolBtn>
        <ToolBtn testId={`${testId}-numbers`} label="Numbered list" active={editor.isActive("orderedList")} onClick={run((c) => c.toggleOrderedList())}><ListOrdered size={16} /></ToolBtn>
        <Sep />
        <ToolBtn testId={`${testId}-link`} label="Link" active={editor.isActive("link") || linking} onClick={() => setLinking((v) => !v)}><Link2 size={15} /></ToolBtn>
        <ToolBtn testId={`${testId}-image`} label="Add image" disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}</ToolBtn>
        <Sep />
        <ToolBtn testId={`${testId}-undo`} label="Undo" disabled={!editor.can().undo()} onClick={run((c) => c.undo())}><Undo2 size={15} /></ToolBtn>
        <ToolBtn testId={`${testId}-redo`} label="Redo" disabled={!editor.can().redo()} onClick={run((c) => c.redo())}><Redo2 size={15} /></ToolBtn>
      </div>
      {linking && <LinkBar editor={editor} testId={testId} onClose={() => setLinking(false)} />}
      <EditorContent editor={editor} />
      <div className="flex flex-wrap justify-between gap-2 rounded-b-lg border-t border-[#F1EEE7] px-3 py-1.5 text-[11px] text-[#7B6157]">
        <span>Images: PNG, JPG, GIF, or WEBP under 5 MB. Do not add client photos or personal details.</span>
        <span data-testid={`${testId}-words`}>{words} words</span>
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" data-testid={`${testId}-image-input`}
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}
